import React from 'react'
import { useTranslation } from 'react-i18next'
import { HiFolder, HiCheckCircle } from 'react-icons/hi'

/**
 * CategoryFilter Component
 * Sidebar for filtering videos by category and difficulty
 */
const CategoryFilter = ({ categories, selected, onSelect, selectedDifficulty, onDifficultyChange }) => {
  const { t } = useTranslation()

  const difficulties = [
    { value: null, label: t('tutorials.filter.allLevels'), color: 'text-gray-700' },
    { value: 'beginner', label: t('tutorials.filter.beginner'), color: 'text-green-600' },
    { value: 'intermediate', label: t('tutorials.filter.intermediate'), color: 'text-yellow-600' },
    { value: 'advanced', label: t('tutorials.filter.advanced'), color: 'text-red-600' }
  ]

  return (
    <div className="bg-white rounded-xl shadow-md p-6 space-y-6 sticky top-24">
      {/* Categories Section */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <HiFolder className="w-5 h-5 text-[#29AECB]" />
          {t('tutorials.filter.categories')}
        </h3>
        <div className="space-y-2">
          {/* All Categories Option */}
          <button
            onClick={() => onSelect(null)}
            className={`w-full text-start px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-between group ${
              selected === null
                ? 'bg-[#29AECB] text-white shadow-md'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <span className="font-medium">{t('tutorials.filter.allCategories')}</span>
            {selected === null && <HiCheckCircle className="w-5 h-5" />}
          </button>

          {/* Category List */}
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => onSelect(category.id)}
              className={`w-full text-start px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-between group ${
                selected === category.id
                  ? 'bg-[#29AECB] text-white shadow-md'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2 flex-1">
                <span className="text-lg">{category.icon || '📁'}</span>
                <div className="flex-1">
                  <div className="font-medium">{category.name}</div>
                  {category.videos_count > 0 && (
                    <div className={`text-xs ${selected === category.id ? 'text-white/80' : 'text-gray-500'}`}>
                      {t('tutorials.filter.videoCount', { count: category.videos_count })}
                    </div>
                  )}
                </div>
              </div>
              {selected === category.id && <HiCheckCircle className="w-5 h-5" />}
            </button>
          ))}
        </div>
      </div>

      {/* Difficulty Filter */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tutorials.filter.difficulty')}</h3>
        <div className="space-y-2">
          {difficulties.map((difficulty) => (
            <button
              key={difficulty.value || 'all'}
              onClick={() => onDifficultyChange(difficulty.value)}
              className={`w-full text-start px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-between ${
                selectedDifficulty === difficulty.value
                  ? 'bg-[#29AECB] text-white shadow-md'
                  : `hover:bg-gray-100 ${difficulty.color}`
              }`}
            >
              <span className="font-medium">{difficulty.label}</span>
              {selectedDifficulty === difficulty.value && <HiCheckCircle className="w-5 h-5" />}
            </button>
          ))}
        </div>
      </div>

      {/* Reset Filters */}
      {(selected !== null || selectedDifficulty !== null) && (
        <button
          onClick={() => {
            onSelect(null)
            onDifficultyChange(null)
          }}
          className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
        >
          {t('tutorials.filter.reset')}
        </button>
      )}
    </div>
  )
}

export default CategoryFilter
